import { Badge, Button, CollapsibleCard, useToast } from '@citizen-walk/shared-ui';
import type { District } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { deleteField, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * 地区の「強い停止（公開状態）」panel (docs/112 §4-1 / Phase 1)。
 *
 * 地区を一時的に一般ユーザーから遮断し、マスター管理者・地区管理者だけがユーザーアプリを
 * 通常どおり利用できる状態（=「強い停止」）に切り替える。停止判定はサーバー側の
 * firestore.rules (`isSuspended`) が `suspended==true` を見て行うため、本 panel は
 * フラグと文言を地区 doc に書込むだけ。
 *
 * 設計（DistrictMiscPanel の作法に倣う）:
 *   - Draft 型 (DistrictPublicStatusDraft) / read (readPublicStatusFromDistrict) /
 *     build (buildPublicStatusPayload) / 保存 (handleSave) の 4 段構成。
 *   - 保存は既存パネル同様 Firestore 直 write (`setDoc merge:true`)。ただし field-scoped
 *     rule (§8-3) が地区管理者の diff を
 *     `hasOnly(['suspended','suspendedAt','suspendedBy','nonPublicMessage','updatedAt','updatedBy'])`
 *     に制限するため、本 panel の write payload は**上記の許可フィールドのみ**に限定する
 *     (DistrictMiscPanel と違い installPromptEnabled 等を混ぜない = 地区管理者でも書込可)。
 *
 * 停止状態の遷移時は本来 operationLogs に監査記録すべき (docs/112 §8-4、`district.suspended` /
 * `district.resumed`) だが、operationLogs は firestore.rules で `allow write: if false`
 * (Functions Admin SDK 専管) のため admin client から直接は書けない。Phase 1 では専用 callable を
 * 新設せず、監査ログは将来の callable 化で対応する。`suspendedAt` / `suspendedBy` を地区 doc に
 * 残すことで最低限の「いつ・誰が停止したか」は追える。
 */

/** pure helper / UI 共用の既定文言 (nonPublicMessage 未設定時にユーザーアプリが出す文言の控え)。 */
export const DEFAULT_NON_PUBLIC_MESSAGE =
  'この地区は現在、一時的に非公開になっています。しばらくたってから、もう一度お試しください。';

export interface DistrictPublicStatusDraft {
  suspended: boolean;
  nonPublicMessage: string;
}

/** pure: District → draft (未設定は未停止 / 文言空)。 */
export function readPublicStatusFromDistrict(d: District): DistrictPublicStatusDraft {
  return {
    suspended: d.suspended === true,
    nonPublicMessage: typeof d.nonPublicMessage === 'string' ? d.nonPublicMessage : '',
  };
}

/** pure: 文言を trim、空なら null (未設定 = 既定文言)。 */
export function normalizeNonPublicMessage(v: string): string | null {
  const t = v.trim();
  return t.length === 0 ? null : t;
}

/**
 * pure: draft → 監査値の組み立て。停止 ON のとき suspendedAt(ISO) / suspendedBy(uid) を付け、
 * OFF のときは両方 null (= 「停止していない」を明示)。前回値ではなく毎回 ON 時に打ち直すことで
 * 「最後に停止操作した時刻・操作者」を表す。
 */
export function buildPublicStatusPayload(
  draft: DistrictPublicStatusDraft,
  uid: string,
  nowIso: string,
): {
  suspended: boolean;
  suspendedAt: string | null;
  suspendedBy: string | null;
  nonPublicMessage: string | null;
} {
  const suspended = !!draft.suspended;
  return {
    suspended,
    suspendedAt: suspended ? nowIso : null,
    suspendedBy: suspended ? uid : null,
    nonPublicMessage: normalizeNonPublicMessage(draft.nonPublicMessage),
  };
}

export interface DistrictPublicStatusPanelProps {
  district: District;
  uid: string;
  /** master または当該地区の地区管理者のとき true (rules の field-scoped 許可と一致)。 */
  canEdit: boolean;
}

export function DistrictPublicStatusPanel({
  district,
  uid,
  canEdit,
}: DistrictPublicStatusPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DistrictPublicStatusDraft>(() =>
    readPublicStatusFromDistrict(district),
  );
  const [saving, setSaving] = useState(false);

  const wasSuspended = district.suspended === true;

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = buildPublicStatusPayload(draft, uid, new Date().toISOString());
      // field-scoped rule (§8-3) に乗せるため、許可フィールド + 監査フィールドのみを書込む。
      // 文言は空なら deleteField で field 削除 (未設定 = ユーザーアプリ既定文言)。
      const writePayload: Record<string, unknown> = {
        suspended: payload.suspended,
        suspendedAt: payload.suspendedAt,
        suspendedBy: payload.suspendedBy,
        nonPublicMessage: payload.nonPublicMessage ?? deleteField(),
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };
      const ref = doc(db, 'districts', district.id);
      await setDoc(ref, writePayload, { merge: true });
      // 監査ログ (operationLogs) は client から直接書けない (rules write:false)。
      // Phase 1 では callable 化せず、suspendedAt / suspendedBy の記録で代替する (上部 JSDoc 参照)。
      const changed = payload.suspended !== wasSuspended;
      toast(
        'success',
        changed
          ? payload.suspended
            ? 'この地区を非公開にしました'
            : 'この地区を公開に戻しました'
          : '公開状態の設定を保存しました',
      );
    } catch {
      toast(
        'error',
        '公開状態の保存に失敗しました。ページを再読み込みして、もう一度お試しください。',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <CollapsibleCard
      title="🚧 公開状態（強い停止）"
      summary={
        wasSuspended ? (
          <Badge variant="danger">非公開中</Badge>
        ) : (
          <Badge variant="success">公開中</Badge>
        )
      }
      storageKey="cw-admin:district-overview:public-status"
      testId="district-public-status-panel"
    >
      <p className="text-xs text-slate-500 mb-3">
        この地区を一時的に一般の参加者から見えない「非公開」に切り替えられます。非公開中も、マスター管理者と地区管理者はこれまでどおりユーザーアプリを利用できます。
      </p>

      <div className="grid grid-cols-1 gap-3">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={draft.suspended}
            onChange={(e) => setDraft({ ...draft, suspended: e.target.checked })}
            disabled={!canEdit || saving}
            className="h-4 w-4 mt-0.5"
            data-testid="public-status-suspended"
          />
          <span className="text-sm">
            この地区を非公開にする（一般ユーザーはアクセス不可。マスター/地区管理者のみ利用可）
          </span>
        </label>

        {draft.suspended && (
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">
              非公開中に一般ユーザーへ表示する文言（任意・未設定なら既定の案内文を表示）
            </span>
            <textarea
              value={draft.nonPublicMessage}
              onChange={(e) => setDraft({ ...draft, nonPublicMessage: e.target.value })}
              disabled={!canEdit || saving}
              rows={3}
              placeholder={DEFAULT_NON_PUBLIC_MESSAGE}
              className="px-3 py-2 border border-slate-300 rounded text-sm bg-white resize-y"
              data-testid="public-status-message"
            />
            <span className="text-[11px] text-slate-400">
              参加者にそのまま見える文章です。担当者名や内部の用語ではなく、利用者に分かる言葉でご記入ください。
            </span>
          </label>
        )}

        {draft.suspended && (
          <p
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
            data-testid="public-status-warning"
          >
            ⚠️
            保存すると、この地区は一般ユーザーからアクセスできなくなります。投稿や情報収集も停止します。公開に戻すには、上のチェックを外して保存してください。
          </p>
        )}
      </div>

      <div className="flex justify-end mt-3">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="public-status-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </CollapsibleCard>
  );
}
